import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  Filter,
  X,
  Star,
  Clock,
  ArrowRight,
  LayoutGrid,
  List,
  TrendingUp
} from 'lucide-react'
import { useCart } from '../context/CartContext'
import api from '../utils/api'
import toast from 'react-hot-toast'

const Tests = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tests, setTests] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '')
  const [viewMode, setViewMode] = useState('grid')
  const [showFilters, setShowFilters] = useState(false)
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const { addToCart, isInCart } = useCart()

  // Auto-suggestion states
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)
  const searchContainerRef = useRef(null)
  const inputRef = useRef(null)
  const debounceRef = useRef(null)

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    fetchTests()
  }, [selectedCategory, searchParams])

  // Handle clicks outside search container to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchCategories = async () => {
    try {
      const response = await api.get('/tests/categories')
      // API returns { success: true, data: [...] }
      const cats = response.data.data || response.data.categories || response.data || []
      setCategories(Array.isArray(cats) ? cats : [])
    } catch (error) {
      console.error('Error fetching categories:', error)
      setCategories([])
    }
  }

  const fetchTests = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (selectedCategory) params.append('category', selectedCategory)
      if (searchQuery) params.append('search', searchQuery)
      params.append('page', searchParams.get('page') || '1')
      params.append('limit', '12')

      const response = await api.get(`/tests?${params.toString()}`)
      // API returns { success: true, data: [...] } or { tests: [...] }
      const testsData = response.data.data || response.data.tests || []
      setTests(Array.isArray(testsData) ? testsData : [])
      setPagination({
        page: response.data.page || 1,
        pages: response.data.pages || 1,
        total: response.data.total || response.data.count || 0
      })
    } catch (error) {
      console.error('Error fetching tests:', error)
      toast.error('Failed to load tests')
      setTests([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch suggestions with debounce
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    setIsLoadingSuggestions(true)
    try {
      const response = await api.get(`/tests?search=${encodeURIComponent(query)}&limit=8`)
      const testsData = response.data.data || response.data.tests || []
      setSuggestions(Array.isArray(testsData) ? testsData : [])
      setShowSuggestions(true)
    } catch (error) {
      console.error('Error fetching suggestions:', error)
      setSuggestions([])
    } finally {
      setIsLoadingSuggestions(false)
    }
  }, [])

  // Handle search input change with debounced suggestions
  const handleSearchInputChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)
    setSelectedIndex(-1)

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    // Debounce the API call for suggestions
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value)
    }, 300)
  }

  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearch(e)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionClick(suggestions[selectedIndex])
        } else {
          handleSearch(e)
        }
        break
      case 'Escape':
        setShowSuggestions(false)
        setSelectedIndex(-1)
        break
      default:
        break
    }
  }

  // Handle suggestion click
  const handleSuggestionClick = (test) => {
    setSearchQuery(test.name)
    setShowSuggestions(false)
    setSelectedIndex(-1)
    // Navigate to test detail page
    navigate(`/tests/${test.slug}`)
  }

  // Handle search as text (not navigating to detail)
  const handleSearchAsText = (testName) => {
    setSearchQuery(testName)
    setShowSuggestions(false)
    setSelectedIndex(-1)
    
    const params = new URLSearchParams(searchParams)
    params.set('search', testName)
    params.set('page', '1')
    setSearchParams(params)
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setShowSuggestions(false)
    const params = new URLSearchParams(searchParams)
    if (searchQuery) {
      params.set('search', searchQuery)
    } else {
      params.delete('search')
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const handleCategoryChange = (category) => {
    setSelectedCategory(category)
    const params = new URLSearchParams(searchParams)
    if (category) {
      params.set('category', category)
    } else {
      params.delete('category')
    }
    params.set('page', '1')
    setSearchParams(params)
    setShowFilters(false)
  }

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', page.toString())
    setSearchParams(params)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleAddToCart = (test) => {
    addToCart(test)
    toast.success(`${test.name} added to cart`)
  }

  const clearFilters = () => {
    setSelectedCategory('')
    setSearchQuery('')
    setSearchParams({})
  }

  // Highlight matching text in suggestions
  const highlightMatch = (text, query) => {
    if (!query) return text
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)
    return parts.map((part, i) => 
      regex.test(part) ? (
        <span key={i} className="font-semibold text-primary-600">{part}</span>
      ) : (
        part
      )
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Lab Test Catalog</h1>
            <p className="text-xl text-primary-100 mb-8">
              Browse our comprehensive selection of lab tests at affordable prices
            </p>
            
            {/* Search Bar with Auto-suggestions */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div ref={searchContainerRef} className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400 z-10" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() => searchQuery.length >= 2 && suggestions.length > 0 && setShowSuggestions(true)}
                  placeholder="Search for tests... (e.g., diabetes, cholesterol, thyroid)"
                  className="w-full pl-12 pr-28 py-4 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-primary-300"
                  autoComplete="off"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  Search
                </button>

                {/* Auto-suggestions Dropdown */}
                <AnimatePresence>
                  {showSuggestions && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50"
                    >
                      {isLoadingSuggestions ? (
                        <div className="px-4 py-6 text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                          <p className="text-sm text-gray-500 mt-2">Searching tests...</p>
                        </div>
                      ) : suggestions.length > 0 ? (
                        <>
                          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                              Suggested Tests ({suggestions.length})
                            </p>
                          </div>
                          <ul className="max-h-80 overflow-y-auto">
                            {suggestions.map((test, index) => (
                              <li key={test._id}>
                                <button
                                  type="button"
                                  onClick={() => handleSuggestionClick(test)}
                                  onMouseEnter={() => setSelectedIndex(index)}
                                  className={`w-full px-4 py-3 text-left flex items-center justify-between gap-3 transition-colors ${
                                    selectedIndex === index
                                      ? 'bg-primary-50'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                      <span className="text-gray-900 truncate">
                                        {highlightMatch(test.name, searchQuery)}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-1 ml-6">
                                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                        {Array.isArray(test.category) ? test.category[0] : test.category}
                                      </span>
                                      {test.isPopular && (
                                        <span className="text-xs text-amber-600 flex items-center">
                                          <TrendingUp className="w-3 h-3 mr-1" />
                                          Popular
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 flex-shrink-0">
                                    <span className="text-primary-600 font-semibold">${test.ourPrice}</span>
                                    <ArrowRight className="w-4 h-4 text-gray-400" />
                                  </div>
                                </button>
                              </li>
                            ))}
                          </ul>
                          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                            <button
                              type="submit"
                              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                            >
                              <Search className="w-4 h-4" />
                              Search all results for "{searchQuery}"
                            </button>
                          </div>
                        </>
                      ) : searchQuery.length >= 2 ? (
                        <div className="px-4 py-6 text-center">
                          <p className="text-gray-600">No tests found for "{searchQuery}"</p>
                          <p className="text-sm text-gray-400 mt-1">Try different keywords</p>
                        </div>
                      ) : null}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </form>

            {/* Popular Searches */}
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <span className="text-primary-200 text-sm">Popular:</span>
              {['Diabetes', 'Thyroid', 'Cholesterol', 'Vitamin D', 'CBC'].map((term) => (
                <button
                  key={term}
                  onClick={() => {
                    setSearchQuery(term)
                    const params = new URLSearchParams(searchParams)
                    params.set('search', term)
                    params.set('page', '1')
                    setSearchParams(params)
                  }}
                  className="text-sm px-3 py-1 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-24">
              <h2 className="font-semibold text-gray-900 mb-4">Categories</h2>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg transition-colors ${
                      !selectedCategory
                        ? 'bg-primary-50 text-primary-600 font-medium'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    All Tests
                    <span className="float-right text-sm text-gray-400">
                      {pagination.total}
                    </span>
                  </button>
                </li>
                {categories.map((cat) => (
                  <li key={cat.name || cat._id}>
                    <button
                      onClick={() => handleCategoryChange(cat.name || cat._id)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg transition-colors ${
                        selectedCategory === (cat.name || cat._id)
                          ? 'bg-primary-50 text-primary-600 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {cat.name || cat._id}
                      <span className="float-right text-sm text-gray-400">
                        {cat.count}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Mobile Filter Button & Controls */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowFilters(true)}
                  className="lg:hidden flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                >
                  <Filter className="w-5 h-5" />
                  Filters
                </button>
                
                {(selectedCategory || searchQuery) && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                  >
                    <X className="w-4 h-4" />
                    Clear filters
                  </button>
                )}
              </div>

              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">
                  {pagination.total} tests found
                </span>
                <div className="hidden sm:flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md ${
                      viewMode === 'grid' ? 'bg-white shadow-sm' : ''
                    }`}
                  >
                    <LayoutGrid className="w-5 h-5 text-gray-600" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md ${
                      viewMode === 'list' ? 'bg-white shadow-sm' : ''
                    }`}
                  >
                    <List className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>

            {/* Active Filters */}
            {selectedCategory && (
              <div className="flex items-center gap-2 mb-6">
                <span className="text-sm text-gray-500">Active filters:</span>
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm">
                  {selectedCategory}
                  <button onClick={() => handleCategoryChange('')}>
                    <X className="w-4 h-4" />
                  </button>
                </span>
              </div>
            )}

            {/* Tests Grid/List */}
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : tests.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No tests found</h3>
                <p className="text-gray-600 mb-4">Try adjusting your search or filters</p>
                <button
                  onClick={clearFilters}
                  className="text-primary-600 font-medium hover:text-primary-700"
                >
                  Clear all filters
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {tests.map((test, index) => (
                  <motion.div
                    key={test._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all overflow-hidden group"
                  >
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <span className="px-3 py-1 bg-primary-50 text-primary-600 text-sm font-medium rounded-full">
                          {Array.isArray(test.category) ? test.category[0] : test.category}
                        </span>
                        {test.isPopular && (
                          <span className="px-2 py-1 bg-amber-50 text-amber-600 text-xs font-medium rounded-full flex items-center">
                            <Star className="w-3 h-3 mr-1 fill-current" />
                            Popular
                          </span>
                        )}
                      </div>
                      
                      <Link to={`/tests/${test.slug}`}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                          {test.name}
                        </h3>
                      </Link>
                      
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {test.description}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {test.turnaroundTime}
                        </span>
                      </div>
                      
                      <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                        <div>
                          <span className="text-2xl font-bold text-primary-600">${test.ourPrice}</span>
                          {test.retailPrice > test.ourPrice && (
                            <span className="text-sm text-gray-400 line-through ml-2">${test.retailPrice}</span>
                          )}
                        </div>
                        
                        {isInCart(test._id) ? (
                          <Link
                            to="/checkout"
                            className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium text-sm hover:bg-green-700 transition-colors"
                          >
                            In Cart ✓
                          </Link>
                        ) : (
                          <button
                            onClick={() => handleAddToCart(test)}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium text-sm hover:bg-primary-700 transition-colors"
                          >
                            Add to Cart
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {tests.map((test, index) => (
                  <motion.div
                    key={test._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-all p-6"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-3 py-1 bg-primary-50 text-primary-600 text-sm font-medium rounded-full">
                            {Array.isArray(test.category) ? test.category[0] : test.category}
                          </span>
                          {test.isPopular && (
                            <span className="px-2 py-1 bg-amber-50 text-amber-600 text-xs font-medium rounded-full flex items-center">
                              <Star className="w-3 h-3 mr-1 fill-current" />
                              Popular
                            </span>
                          )}
                        </div>
                        <Link to={`/tests/${test.slug}`}>
                          <h3 className="text-lg font-semibold text-gray-900 hover:text-primary-600 transition-colors">
                            {test.name}
                          </h3>
                        </Link>
                        <p className="text-gray-600 text-sm mt-1">{test.description}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {test.turnaroundTime}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <span className="text-2xl font-bold text-primary-600">${test.ourPrice}</span>
                          {test.retailPrice > test.ourPrice && (
                            <p className="text-sm text-gray-400 line-through">${test.retailPrice}</p>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <Link
                            to={`/tests/${test.slug}`}
                            className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors"
                          >
                            Details
                          </Link>
                          {isInCart(test._id) ? (
                            <Link
                              to="/checkout"
                              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium text-sm hover:bg-green-700 transition-colors"
                            >
                              In Cart ✓
                            </Link>
                          ) : (
                            <button
                              onClick={() => handleAddToCart(test)}
                              className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium text-sm hover:bg-primary-700 transition-colors"
                            >
                              Add
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center mt-12">
                <nav className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  {[...Array(pagination.pages)].map((_, i) => {
                    const page = i + 1
                    if (
                      page === 1 ||
                      page === pagination.pages ||
                      (page >= pagination.page - 1 && page <= pagination.page + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`w-10 h-10 rounded-lg font-medium ${
                            pagination.page === page
                              ? 'bg-primary-600 text-white'
                              : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    } else if (
                      page === pagination.page - 2 ||
                      page === pagination.page + 2
                    ) {
                      return <span key={page} className="px-2">...</span>
                    }
                    return null
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      <AnimatePresence>
        {showFilters && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setShowFilters(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween' }}
              className="fixed left-0 top-0 bottom-0 w-80 bg-white z-50 lg:hidden overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Filters</h2>
                  <button onClick={() => setShowFilters(false)}>
                    <X className="w-6 h-6 text-gray-400" />
                  </button>
                </div>

                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">Categories</h3>
                  <ul className="space-y-2">
                    <li>
                      <button
                        onClick={() => handleCategoryChange('')}
                        className={`w-full text-left px-4 py-2.5 rounded-lg transition-colors ${
                          !selectedCategory
                            ? 'bg-primary-50 text-primary-600 font-medium'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        All Tests
                      </button>
                    </li>
                    {categories.map((cat) => (
                      <li key={cat.name || cat._id}>
                        <button
                          onClick={() => handleCategoryChange(cat.name || cat._id)}
                          className={`w-full text-left px-4 py-2.5 rounded-lg transition-colors ${
                            selectedCategory === (cat.name || cat._id)
                              ? 'bg-primary-50 text-primary-600 font-medium'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {cat.name || cat._id}
                          <span className="float-right text-sm text-gray-400">
                            {cat.count}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Tests